/**
 * @fileoverview Zcash Wallet Service
 * Provides integration with Zcash wallets for NFPT platform.
 * Supports Winbit32 (built-in mnemonic) and Manual (external wallet) modes.
 */

import winbit32Wallet, { type ScanProgress } from './winbit32Wallet';
import { getCosignWalletService } from './cosignWallet';
import {
	saveMnemonic,
	loadUnprotectedMnemonic,
	unlockMnemonic,
	clearMnemonic,
	getStoredMnemonicInfo,
	migrateLegacyPlaintext,
	type MnemonicStorageMode,
	type StoredMnemonicInfo,
} from './walletVault';

export interface ZcashAddress {
	shielded: string;
	transparent: string;
}

export interface ZcashBalance {
	shielded: number;
	transparent: number;
	total: number;
}

export interface TransactionData {
	recipient: string;
	amount: number;
	memo?: string;
	fee?: number;
	/**
	 * Allow the in-browser signer to spend transparent (t-address) UTXOs when
	 * the shielded (Orchard) note balance can't cover the amount. The transparent
	 * inputs are spent straight into the shielded output, so this is a one-step
	 * "shield-and-send" (auto-shield). Only honoured by the winbit32 signer.
	 */
	allowTransparentInputs?: boolean;
	/**
	 * Skip the (potentially slow) Orchard note scan entirely and spend
	 * transparent funds straight away. Implies `allowTransparentInputs`. Used by
	 * the mint flow's "Shield from transparent and send" shortcut. winbit32 only.
	 */
	skipShieldedScan?: boolean;
	/**
	 * Lets the caller cancel an in-flight send (specifically the note scan) —
	 * e.g. when the user chooses to shield from transparent instead of waiting.
	 * winbit32 only.
	 */
	abortSignal?: AbortSignal;
}

export interface SignedTransaction {
	txid: string;
	raw: string;
	success: boolean;
}

export interface WalletConnection {
	address: ZcashAddress;
	balance: ZcashBalance;
	isConnected: boolean;
	walletType: string;
}

export interface WalletError {
	code: string;
	message: string;
	details?: any;
}

export type SupportedWalletType = 'manual' | 'winbit32' | 'cosign';

export interface Winbit32StorageOptions {
	mode: MnemonicStorageMode;
	password?: string;
}

/**
 * Coarse phase of a co-signing (FROST) ceremony, surfaced app-wide so a single
 * overlay can show the pairing QR and live status wherever a cosign send is
 * triggered (e.g. minting). Mirrors the hook's local phases but is owned by the
 * service so the channel survives modal close.
 */
export type CosignCeremonyPhase =
	| 'idle'
	| 'preparing'
	| 'awaiting-cosigner'
	| 'signing'
	| 'broadcasting'
	| 'done'
	| 'error';

export interface CosignCeremonyState {
	/** True while an overlay should be shown (preparing → done/error window). */
	active: boolean;
	phase: CosignCeremonyPhase;
	message: string;
	/** WB32COSIGN pairing payload to render as a QR while awaiting the cosigner. */
	qrPayload: string | null;
	txid: string | null;
	error: string | null;
}

const IDLE_COSIGN_CEREMONY: CosignCeremonyState = {
	active: false,
	phase: 'idle',
	message: '',
	qrPayload: null,
	txid: null,
	error: null,
};

/** Map a co-sign progress line to a coarse ceremony phase (best-effort). */
const cosignPhaseForMessage = (msg: string, current: CosignCeremonyPhase): CosignCeremonyPhase => {
	if (/signing action|round \d|aggregat|setup/i.test(msg)) return 'signing';
	if (/proving|broadcast|submitting/i.test(msg)) return 'broadcasting';
	if (/scanning|building/i.test(msg)) {
		return current === 'awaiting-cosigner' ? 'awaiting-cosigner' : 'preparing';
	}
	return current;
};

/**
 * Raised by `connect('winbit32')` when an encrypted seed is stored but no
 * decrypted copy is held in memory yet — the UI should prompt for the password
 * and call `unlockWinbit32()`.
 */
export const WALLET_LOCKED_CODE = 'WALLET_LOCKED';

/**
 * Main Zcash Wallet Service Class
 */
export class ZcashWalletService {
	private static instance: ZcashWalletService;
	private walletType: SupportedWalletType | null = null;
	private walletInstance: any = null;
	private connectionState: WalletConnection | null = null;
	// Decrypted mnemonic held in memory for the lifetime of the tab so the user
	// isn't re-prompted for their password on every reconnect. Never persisted
	// from here — durable storage is handled solely by walletVault.
	private memoryMnemonic: string | null = null;

	// Connection-change subscribers. Lets a passive UI (the bottom wallet bar)
	// reflect connect/disconnect without prop-drilling or polling. Kept separate
	// from the scan-progress channel so it can stay valid for a future
	// standalone Winbit32 wallet module/extension.
	private readonly connectionListeners = new Set<() => void>();

	// Co-signing (FROST) ceremony channel. A loaded `.wult` share can pay for a
	// mint by co-signing with a second device; this state drives the app-wide
	// pairing-QR + status overlay. Kept on the singleton so it's independent of
	// any one modal's lifetime.
	private cosignCeremony: CosignCeremonyState = { ...IDLE_COSIGN_CEREMONY };
	private readonly cosignCeremonyListeners = new Set<() => void>();
	private cosignAbort: AbortController | null = null;
	private cosignCeremonyClearTimer: number | null = null;

	private constructor() {}

	/**
	 * Subscribe to connect / disconnect changes. Returns an unsubscribe fn.
	 * Listeners re-read state via {@link getConnection}; we intentionally pass
	 * no payload so there's a single source of truth.
	 */
	public onConnectionChange(listener: () => void): () => void {
		this.connectionListeners.add(listener);
		return () => {
			this.connectionListeners.delete(listener);
		};
	}

	private emitConnectionChange(): void {
		for (const listener of this.connectionListeners) {
			try {
				listener();
			} catch (err) {
				console.warn('Wallet connection listener failed', err);
			}
		}
	}

	/** Current co-signing ceremony state (for the app-wide pairing overlay). */
	public getCosignCeremony(): CosignCeremonyState {
		return this.cosignCeremony;
	}

	/** Subscribe to co-signing ceremony changes. Returns an unsubscribe fn. */
	public onCosignCeremonyChange(listener: () => void): () => void {
		this.cosignCeremonyListeners.add(listener);
		return () => {
			this.cosignCeremonyListeners.delete(listener);
		};
	}

	private setCosignCeremony(partial: Partial<CosignCeremonyState>): void {
		this.cosignCeremony = { ...this.cosignCeremony, ...partial };
		for (const listener of this.cosignCeremonyListeners) {
			try {
				listener();
			} catch (err) {
				console.warn('Cosign ceremony listener failed', err);
			}
		}
	}

	private scheduleCosignCeremonyClear(ms: number): void {
		if (this.cosignCeremonyClearTimer) {
			clearTimeout(this.cosignCeremonyClearTimer);
		}
		this.cosignCeremonyClearTimer = (typeof window !== 'undefined' ? window.setTimeout : setTimeout)(() => {
			this.cosignCeremonyClearTimer = null;
			this.setCosignCeremony({ ...IDLE_COSIGN_CEREMONY });
		}, ms) as unknown as number;
	}

	/** Abort an in-flight ceremony (user cancelled the pairing) and hide it. */
	public cancelCosignCeremony(): void {
		this.cosignAbort?.abort();
		this.dismissCosignCeremony();
	}

	/** Hide the ceremony overlay (e.g. after the user acknowledges done/error). */
	public dismissCosignCeremony(): void {
		if (this.cosignCeremonyClearTimer) {
			clearTimeout(this.cosignCeremonyClearTimer);
			this.cosignCeremonyClearTimer = null;
		}
		this.setCosignCeremony({ ...IDLE_COSIGN_CEREMONY });
	}

	public static getInstance(): ZcashWalletService {
		if (!ZcashWalletService.instance) {
			ZcashWalletService.instance = new ZcashWalletService();
		}
		return ZcashWalletService.instance;
	}

	public getAvailableWallets(): SupportedWalletType[] {
		return ['winbit32', 'manual'];
	}

	public async connect(walletType: SupportedWalletType): Promise<WalletConnection> {
		try {
			switch (walletType) {
				case 'winbit32':
					return await this.connectWinbit32();
				case 'manual':
					return await this.connectManual();
				case 'cosign':
					return await this.connectCosign();
				default:
					throw new Error(`Unsupported wallet type: ${walletType}`);
			}
		} catch (error) {
			throw this.handleError(error as any, 'WALLET_CONNECTION_FAILED');
		}
	}

	private async connectWinbit32(): Promise<WalletConnection> {
		// Resolve the seed in priority order: in-memory (already unlocked this
		// tab) → session-mode plaintext → migrate a legacy plaintext install.
		let mnemonic = this.memoryMnemonic
			|| loadUnprotectedMnemonic()
			|| migrateLegacyPlaintext();

		if (!mnemonic) {
			const info = getStoredMnemonicInfo();
			if (info.exists && info.encrypted) {
				// Seed is stored but locked — caller must supply the password.
				const err = new Error('Your saved wallet is locked. Enter your password to unlock it.') as any;
				err.code = WALLET_LOCKED_CODE;
				throw err;
			}
			throw new Error('Winbit32 wallet requires a mnemonic. Import or generate one first.');
		}

		this.memoryMnemonic = mnemonic;
		return this.finaliseWinbit32Connection(mnemonic);
	}

	private async finaliseWinbit32Connection(mnemonic: string): Promise<WalletConnection> {
		const address = await winbit32Wallet.connect(mnemonic);
		// connect() already kicks off a background balance scan.
		const balance = winbit32Wallet.getBalance();

		this.walletType = 'winbit32';
		this.walletInstance = winbit32Wallet;
		this.connectionState = {
			address,
			balance,
			isConnected: true,
			walletType: 'winbit32',
		};
		this.emitConnectionChange();
		return this.connectionState;
	}

	/**
	 * Decrypt the stored seed with `password`, cache it in memory and connect.
	 * Used by the "unlock" prompt when an encrypted seed is present.
	 */
	public async unlockWinbit32(password: string): Promise<WalletConnection> {
		try {
			const mnemonic = await unlockMnemonic(password);
			this.memoryMnemonic = mnemonic;
			return await this.finaliseWinbit32Connection(mnemonic);
		} catch (error) {
			throw this.handleError(error as any, 'WALLET_UNLOCK_FAILED');
		}
	}

	private async connectManual(): Promise<WalletConnection> {
		const storedConnection = localStorage.getItem('manual_wallet_connection');
		if (!storedConnection) {
			throw new Error('Manual wallet connection requires address input');
		}

		const { shieldedAddress, transparentAddress } = JSON.parse(storedConnection);

		if (!shieldedAddress) {
			throw new Error('Shielded address is required for manual connection');
		}

		this.walletType = 'manual';
		this.walletInstance = null;
		this.connectionState = {
			address: {
				shielded: shieldedAddress,
				transparent: transparentAddress || ''
			},
			balance: {
				shielded: 0,
				transparent: 0,
				total: 0
			},
			isConnected: true,
			walletType: 'manual'
		};

		this.emitConnectionChange();
		return this.connectionState;
	}

	/**
	 * Sign in with a co-signing (`.wult`) share that's already loaded into the
	 * shared {@link CosignWalletService}. The derived unified address becomes the
	 * connected wallet so the header shows "connected" and mint owner fields
	 * auto-fill; payments are then routed through the FROST ceremony.
	 */
	public async connectCosign(): Promise<WalletConnection> {
		const svc = getCosignWalletService();
		const vault = svc.getWallet();
		if (!vault) {
			throw new Error('Load a co-signing share before signing in.');
		}
		// Preserve a previously-scanned cosign balance across a reconnect.
		const balance = this.walletType === 'cosign' && this.connectionState
			? this.connectionState.balance
			: { shielded: 0, transparent: 0, total: 0 };

		this.walletType = 'cosign';
		this.walletInstance = svc;
		this.connectionState = {
			address: { shielded: vault.unifiedAddress, transparent: '' },
			balance,
			isConnected: true,
			walletType: 'cosign',
		};
		this.emitConnectionChange();
		return this.connectionState;
	}

	/**
	 * Push a freshly-scanned cosign shielded balance into the connection so the
	 * header/owner UI reflects real funds. The co-sign modal owns the (slow)
	 * view-only scan and calls this when it completes.
	 */
	public updateCosignBalance(shieldedZec: number): void {
		if (this.walletType !== 'cosign' || !this.connectionState) return;
		const shielded = Math.max(0, shieldedZec);
		this.connectionState.balance = { shielded, transparent: 0, total: shielded };
		this.emitConnectionChange();
	}

	public setManualAddresses(shieldedAddress: string, transparentAddress: string = ''): void {
		localStorage.setItem('manual_wallet_connection', JSON.stringify({
			shieldedAddress,
			transparentAddress
		}));
	}

	public clearManualAddresses(): void {
		localStorage.removeItem('manual_wallet_connection');
	}

	/**
	 * Store the seed for signing. Defaults to SESSION storage (cleared when the
	 * tab closes, never written to durable localStorage); pass
	 * `{ mode: 'persistent', password }` to remember it across sessions
	 * (always encrypted). A `session` password is optional but encrypts the
	 * session copy too. The decrypted seed is also cached in memory so the
	 * immediately-following `connect('winbit32')` succeeds without a prompt.
	 */
	public async setWinbit32Mnemonic(
		mnemonic: string,
		options: Winbit32StorageOptions = { mode: 'session' }
	): Promise<void> {
		const normalised = (mnemonic || '').trim().replace(/\s+/g, ' ');
		if (!normalised) {
			throw new Error('Mnemonic is required');
		}
		await saveMnemonic(normalised, options);
		this.memoryMnemonic = normalised;
	}

	public clearWinbit32Mnemonic(): void {
		clearMnemonic();
		this.memoryMnemonic = null;
	}

	public getWinbit32Mnemonic(): string | null {
		return this.memoryMnemonic || loadUnprotectedMnemonic();
	}

	/** Describe the stored seed so the UI can pick enter-seed vs unlock vs auto. */
	public getStoredMnemonicInfo(): StoredMnemonicInfo {
		return getStoredMnemonicInfo();
	}

	public async disconnect(): Promise<void> {
		const wasCosign = this.walletType === 'cosign';
		if (this.walletInstance && this.walletType === 'winbit32') {
			this.walletInstance.disconnect();
		}
		if (wasCosign) {
			// Drop the decrypted share + cancel any in-flight ceremony.
			try { getCosignWalletService().forget(); } catch (e) { console.warn('Cosign forget failed', e); }
			this.cancelCosignCeremony();
		}

		// Drop every byte of wallet-derived state from the browser so the
		// "Forget this seed" affordance actually means it. Without this the
		// mnemonic + manual address survives in localStorage and is
		// recoverable on the next page load (or by any same-origin XSS).
		// A *cosign* disconnect only forgets the in-memory share above — it must
		// not nuke a separately-saved winbit32 seed/manual address.
		if (!wasCosign) {
			try {
				this.clearWinbit32Mnemonic();
				this.clearManualAddresses();
			} catch (cleanupError) {
				console.warn('Wallet disconnect cleanup partially failed', cleanupError);
			}
		}

		this.walletType = null;
		this.walletInstance = null;
		this.connectionState = null;
		this.emitConnectionChange();
	}

	public isConnected(): boolean {
		return this.connectionState?.isConnected || false;
	}

	public getConnection(): WalletConnection | null {
		return this.connectionState;
	}

	public getAddresses(): ZcashAddress | null {
		return this.connectionState?.address || null;
	}

	public async getBalance(): Promise<ZcashBalance> {
		if (!this.isConnected()) {
			throw new Error('Wallet not connected');
		}

		try {
			switch (this.walletType) {
				case 'manual':
				case 'cosign':
					// Cosign balance comes from the modal's view-only scan (pushed via
					// updateCosignBalance); avoid a slow blocking re-scan here.
					return this.connectionState?.balance || { shielded: 0, transparent: 0, total: 0 };

				case 'winbit32': {
					const balance = await this.walletInstance.refreshBalance();
					if (this.connectionState) {
						this.connectionState.balance = balance;
					}
					return balance;
				}

				default:
					throw new Error(`Balance retrieval not implemented for ${this.walletType}`);
			}
		} catch (error) {
			throw this.handleError(error as any, 'BALANCE_FETCH_FAILED');
		}
	}

	public async createShieldedTransaction(txData: TransactionData): Promise<SignedTransaction> {
		if (!this.isConnected()) {
			throw new Error('Wallet not connected');
		}

		try {
			this.validateTransactionData(txData);

			switch (this.walletType) {
				case 'winbit32':
					return await this.createWinbit32Transaction(txData);
				case 'cosign':
					return await this.createCosignTransaction(txData);
				case 'manual':
					return await this.createManualTransaction(txData);
				default:
					throw new Error(`Transaction creation not implemented for ${this.walletType}`);
			}
		} catch (error) {
			throw this.handleError(error as any, 'TRANSACTION_CREATION_FAILED');
		}
	}

	private async createManualTransaction(txData: TransactionData): Promise<SignedTransaction> {
		const manualTransactionData = {
			recipient: txData.recipient,
			amount: txData.amount,
			memo: txData.memo || '',
			description: 'Complete this transaction manually in your Zcash wallet'
		};

		const error = new Error('MANUAL_TRANSACTION_REQUIRED') as any;
		error.transactionDetails = manualTransactionData;
		throw error;
	}

	private async createWinbit32Transaction(txData: TransactionData): Promise<SignedTransaction> {
		const result = await this.walletInstance.send(txData.recipient, txData.amount, txData.memo, {
			allowTransparentInputs: txData.allowTransparentInputs === true,
			skipShieldedScan: txData.skipShieldedScan === true,
			abortSignal: txData.abortSignal,
		});
		if (this.connectionState) {
			this.connectionState.balance = this.walletInstance.getBalance();
		}
		return result;
	}

	/**
	 * Pay via the Orchard FROST co-signing ceremony. Drives the app-wide overlay
	 * (pairing QR → live status) through {@link setCosignCeremony} while the
	 * shared {@link CosignWalletService} scans, builds, co-signs and broadcasts.
	 * The mint flow awaits the returned txid exactly as it does for winbit32.
	 */
	private async createCosignTransaction(txData: TransactionData): Promise<SignedTransaction> {
		const svc = getCosignWalletService();
		if (!svc.isLoaded()) {
			throw new Error('Load your co-signing share before sending.');
		}
		const controller = new AbortController();
		this.cosignAbort = controller;
		this.setCosignCeremony({
			active: true,
			phase: 'preparing',
			message: 'Preparing co-signing session…',
			qrPayload: null,
			txid: null,
			error: null,
		});
		try {
			const result = await svc.send({
				toAddress: txData.recipient.trim(),
				amountZec: txData.amount,
				memo: txData.memo?.trim() || undefined,
				onQrReady: (qrPayload) => this.setCosignCeremony({
					phase: 'awaiting-cosigner',
					qrPayload,
					message: 'Scan this with the Winbit32 cosigner on your other device.',
				}),
				callbacks: {
					signal: controller.signal,
					onProgress: (message) => this.setCosignCeremony({
						message,
						phase: cosignPhaseForMessage(message, this.cosignCeremony.phase),
					}),
				},
			});
			this.setCosignCeremony({
				phase: 'done',
				txid: result.txid,
				qrPayload: null,
				message: 'Transaction broadcast.',
			});
			this.scheduleCosignCeremonyClear(5000);
			return { txid: result.txid, raw: result.signedPczt, success: true };
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Co-signing failed.';
			this.setCosignCeremony({ phase: 'error', error: message, message, qrPayload: null });
			this.scheduleCosignCeremonyClear(9000);
			throw error;
		} finally {
			this.cosignAbort = null;
		}
	}

	private validateTransactionData(txData: TransactionData): void {
		if (!txData.recipient) {
			throw new Error('Recipient address is required');
		}

		if (!this.isValidZcashAddress(txData.recipient)) {
			throw new Error('Invalid recipient address format');
		}

		if (txData.amount <= 0) {
			throw new Error('Amount must be greater than zero');
		}

		if (txData.memo && txData.memo.length > 512) {
			throw new Error('Memo exceeds maximum length of 512 bytes');
		}
	}

	private isValidZcashAddress(address: string): boolean {
		if (address.startsWith('zs1') || address.startsWith('u1')) {
			return address.length >= 78;
		}
		if (address.startsWith('t1') || address.startsWith('t3')) {
			return address.length >= 34 && address.length <= 35;
		}
		return false;
	}

	private handleError(error: any, code: string): WalletError {
		const walletError: WalletError = {
			code,
			message: error.message || 'Unknown wallet error',
			details: error
		};

		if (error.message?.includes('User rejected')) {
			walletError.code = 'USER_REJECTED';
			walletError.message = 'Transaction rejected by user';
		} else if (error.message?.includes('Insufficient funds')) {
			walletError.code = 'INSUFFICIENT_FUNDS';
			walletError.message = 'Insufficient funds for transaction';
		} else if (error.message?.includes('Network')) {
			walletError.code = 'NETWORK_ERROR';
			walletError.message = 'Network connection error';
		}

		return walletError;
	}

	public getScanProgress(): ScanProgress {
		return winbit32Wallet.getScanProgress();
	}

	public onScanProgress(callback: (progress: ScanProgress) => void): () => void {
		return winbit32Wallet.onScanProgress(callback);
	}
}

export const zcashWallet = ZcashWalletService.getInstance();

export const formatZcashAmount = (amount: number): string => {
	return `${amount.toFixed(6)} ZEC`;
};

export const truncateAddress = (address: string, length: number = 8): string => {
	if (address.length <= length * 2) return address;
	return `${address.slice(0, length)}...${address.slice(-length)}`;
};

export const validateMemoSize = (memo: string): boolean => {
	return new TextEncoder().encode(memo).length <= 512;
};
